import { useState } from "react";
import debounce from "../../util/debounce";
import { useMutation, useQueryClient } from "react-query";
import { checkUserId, signUp } from "../../api";
import PasswordInput from "./PasswordInput";

const SignUpForm = ({ error, setError, agree, afterSave }) => {
  const queryClient = useQueryClient();
  const [data, setData] = useState({
    user_id: "",
    password: "",
    user_name: "",
  });
  const [isAvailable, setAvailable] = useState({
    id: false,
    pw: false,
    name: false,
  });
  const [msg, setMessage] = useState({ id: "", pw: "", name: "" });
  const [focusInput, setFocus] = useState(null);

  const { mutate: checkMutation } = useMutation({
    mutationFn: checkUserId,
    onSettled: (data) => {
      if (data && data.result) {
        setMessage({ ...msg, id: "사용 가능한 아이디입니다." });
        return setAvailable({ ...isAvailable, id: true });
      }
      setMessage({ ...msg, id: data.error });
      setAvailable({ ...isAvailable, id: false });

      queryClient.invalidateQueries({ queryKey: ["user/checkId"] });
    },
  });

  const { mutate: saveMutation } = useMutation({
    mutationFn: signUp,
    onSettled: (data) => {
      if (data && data.result) {
        return afterSave(data.data);
      }
      setError("회원가입 중 문제가 발생했습니다.");
      queryClient.invalidateQueries({ queryKey: ["user/signup"] });
    },
  });

  // 아이디 유효성 검사 함수
  const validateUserId = (userId) => {
    return new RegExp("^[a-z]+[a-z0-9_]{3,}$").test(userId);
  };

  // 비밀번호 유효성 검사 함수
  const validatePassword = (password) => {
    const score = ["[a-z]", "[A-Z]", "[!@#$%^&*,.?]", "[0-9]"].reduce(
      (ac, cu) => {
        const result = new RegExp(cu, "g").test(password);
        return result ? ac + 1 : ac;
      },
      0
    );
    return score > 1 && password.length > 7;
  };

  // 이름 유효성 검사 함수
  const validateName = (name) => {
    return new RegExp("[a-zA-Z가-힣]{2,}").test(name);
  };

  const checkUserIdAvailabilityDebounced = debounce(async (v) => {
    if (v) {
      if (!validateUserId(v)) {
        setMessage({
          ...msg,
          id: "아이디는 영문 소문자 및 숫자로 4자 이상 입력하세요.",
        });
        return setAvailable({ ...isAvailable, id: false });
      }
      checkMutation(v);
    } else {
      setMessage({ ...msg, id: "아이디를 입력하세요." });
      setAvailable({ ...isAvailable, id: false });
    }
  }, 300);

  const handleChange = (e) => {
    const { value: val, id: target } = e.target;
    setFocus(target);
    let value = target === "user_id" ? val.toLowerCase() : val;
    setData({ ...data, [target]: value });
    setError("");
    if (target === "user_id") {
      setAvailable({ ...isAvailable, id: false });
      setMessage({ ...msg, id: "" });
      checkUserIdAvailabilityDebounced(value);
    } else if (target === "password") {
      if (validatePassword(value)) {
        setAvailable({ ...isAvailable, pw: true });
        setMessage({ ...msg, pw: "" });
      } else {
        setAvailable({ ...isAvailable, pw: false });
        setMessage({
          ...msg,
          pw: "비밀번호는 영문 소문자 및 대문자, 숫자, 특수문자(!@#$%^&*,.?)를 두가지 이상 조합하여 8자 이상 입력하세요.",
        });
      }
    } else {
      if (validateName(value)) {
        setAvailable({ ...isAvailable, name: true });
        setMessage({ ...msg, name: "" });
      } else {
        setAvailable({ ...isAvailable, name: false });
        setMessage({
          ...msg,
          name: "이름은 한글 및 영문으로 2자 이상 입력하세요.",
        });
      }
    }
  };

  const handlePasswordChange = (e, msg) => {
    const { value, id: target } = e.target;
    setFocus(target);
    setData({ ...data, [target]: value });
    setError("");
    setAvailable({ ...isAvailable, pw: !msg });
    setMessage({ ...msg, pw: msg });
  };

  const handleBlur = () => {
    setFocus(null);
  };

  const handleNext = () => {
    const { user_id: id, password, user_name: name } = data;
    setError(null);
    saveMutation({ id, password, name, agree, role: "student" });
  };

  const handleKeyUp = (e) => {
    if (e.keyCode === 13) {
      handleNext();
    }
  };

  return (
    <div className="card-body" onKeyUp={handleKeyUp}>
      <div className="form-control">
        <input
          id="user_name"
          type="text"
          placeholder="이름"
          className={`input input-bordered${
            isAvailable.name ? " border-primary" : ""
          }`}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <div className="flex flex-col mt-1">
          {data.user_name && !isAvailable.name && (
            <span className="text-sm text-red-500">{msg.name}</span>
          )}
          {data.user_name && isAvailable.name && (
            <span className="text-sm text-green-500">{msg.name}</span>
          )}
          {!data.user_name && (
            <>
              <span className="text-xs">실명을 입력하세요.</span>
            </>
          )}
        </div>
      </div>
      <div className="form-control">
        <input
          id="user_id"
          type="text"
          placeholder="아이디"
          className={`input input-bordered${
            isAvailable.id ? " border-primary" : ""
          }`}
          value={data.user_id}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <div className="flex flex-col mt-1">
          {data.user_id && !isAvailable.id && (
            <span className="text-sm text-red-500">{msg.id}</span>
          )}
          {data.user_id && isAvailable.id && focusInput === "user_id" && (
            <span className="text-sm text-green-500">{msg.id}</span>
          )}
        </div>
      </div>
      <PasswordInput
        {...{
          id: "password",
          placeholder: "비밀번호",
          value: data.password,
          onBlur: handleBlur,
          onChange: handlePasswordChange,
        }}
      />
      {error && (
        <div className="inline-flex shadow-sm p-2 rounded-md items-center bg-red-500 border border-red-500 text-red-500 bg-opacity-10">
          <div className="inline-flex gap-2 items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="stroke-current flex-shrink-0 h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}
      <div className="form-control mt-6">
        <button
          className="btn btn-primary"
          disabled={!(isAvailable.id && isAvailable.name && isAvailable.pw)}
          onClick={handleNext}
        >
          회원가입
        </button>
      </div>
    </div>
  );
};

export default SignUpForm;
